/**
 * playBell(count, context?)
 *
 * Plays N singing-bowl-style bell tones using Web Audio API.
 * No audio files required - synthesised on device.
 *
 * Usage:
 *   import { playBell } from '../utils/bell';
 *   await playBell(3);  // 3 bells at step transition
 *   await playBell(1);  // 1 bell at session end
 */

const BELL_FREQ   = 432;   // Hz - close to A4, calm and centred
const DECAY       = 3.0;   // seconds per bell tone
const BELL_GAP    = 1200;  // ms between bells

export const playBell = (count = 1) => {
  return new Promise((resolve) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();

      const ringOne = (delay) => {
        // Fundamental
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.type = 'sine';
        osc.frequency.setValueAtTime(BELL_FREQ, ctx.currentTime + delay);

        gain.gain.setValueAtTime(0, ctx.currentTime + delay);
        gain.gain.linearRampToValueAtTime(0.4, ctx.currentTime + delay + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + DECAY);

        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + DECAY);

        // Second partial (octave + fifth) for bowl warmth
        const osc2  = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.connect(gain2);
        gain2.connect(ctx.destination);

        osc2.type = 'sine';
        osc2.frequency.setValueAtTime(BELL_FREQ * 2.75, ctx.currentTime + delay);

        gain2.gain.setValueAtTime(0, ctx.currentTime + delay);
        gain2.gain.linearRampToValueAtTime(0.12, ctx.currentTime + delay + 0.01);
        gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + DECAY * 0.6);

        osc2.start(ctx.currentTime + delay);
        osc2.stop(ctx.currentTime + delay + DECAY);
      };

      for (let i = 0; i < count; i++) {
        ringOne(i * (BELL_GAP / 1000));
      }

      // Resolve after all bells + last decay
      const totalMs = (count - 1) * BELL_GAP + DECAY * 1000;
      setTimeout(() => {
        ctx.close().catch(() => {});
        resolve();
      }, totalMs + 200);

    } catch {
      resolve(); // never block the caller
    }
  });
};
